import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useI18n } from "../../i18n/I18nContext";

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Login() {
  const navigate = useNavigate();
  const { openCart } = useCart();
  const { login } = useAuth();
  const { t } = useI18n();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);

  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const localCart = JSON.parse(localStorage.getItem("cart") || "[]");

      const cart_items = Array.isArray(localCart)
        ? localCart.map((i) => ({
            product_variant_id: Number(i.variant_id ?? i.id),
            quantity: Number(i.quantity),
          }))
        : [];

      const res = await axios.post(`${API_BASE_URL}/api/login`, {
        email,
        password,
        cart_items,
      });

      const token = res.data.token;
      const user = res.data.user;

      login(user, token);
      localStorage.removeItem("cart");

      const returnTo = localStorage.getItem("returnTo");
      if (returnTo) localStorage.removeItem("returnTo");

      const isAdmin = user?.roles?.some((r) => r.name === "admin");

      toast.success(t("Login efetuado com sucesso!"));

      if (isAdmin) {
        navigate("/admin/users");
      } else if (returnTo) {
        navigate(returnTo);
        openCart();
      } else {
        navigate("/products");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        t("Erro no login. Confirma email e password.");

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const openForgotPasswordModal = () => {
    setForgotEmail(email || "");
    setShowForgotModal(true);
  };

  const closeForgotPasswordModal = () => {
    if (forgotLoading) return;

    setShowForgotModal(false);
    setForgotEmail("");
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/api/forgot-password`, {
        email: forgotEmail,
      });

      toast.success(
        t(
          "Se este email existir, foi enviado um link para redefinir a password."
        )
      );

      setShowForgotModal(false);
      setForgotEmail("");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        t("Não foi possível enviar o email de recuperação.");

      toast.error(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold mb-6 text-center">{t("Entrar")}</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("Email")}
            </label>

            <input
              type="email"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t("Password")}
            </label>

            <input
              type="password"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />

            <div className="mt-3 text-center">
              <button
                type="button"
                className="text-sm text-indigo-600 font-medium hover:underline"
                onClick={openForgotPasswordModal}
              >
                {t("Esqueceste-te da password?")}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white rounded py-2 font-semibold disabled:opacity-60"
          >
            {loading ? t("A entrar...") : t("Entrar")}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          {t("Ainda não tens conta?")}{" "}
          <button
            type="button"
            className="text-indigo-600 font-semibold hover:underline"
            onClick={() => navigate("/register")}
          >
            {t("Registar")}
          </button>
        </div>
      </div>

      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl p-8 relative">
            <button
              type="button"
              className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-2xl leading-none"
              onClick={closeForgotPasswordModal}
              disabled={forgotLoading}
              aria-label={t("Fechar")}
            >
              ×
            </button>

            <h2 className="text-2xl font-bold mb-2 text-center">
              {t("Recuperar password")}
            </h2>

            <p className="text-sm text-gray-600 text-center mb-6">
              {t(
                "Introduz o teu email e enviaremos um link para redefinires a tua password."
              )}
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t("Email")}
                </label>

                <input
                  type="email"
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  autoComplete="email"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={forgotLoading}
                className="w-full bg-gray-900 text-white rounded py-2 font-semibold disabled:opacity-60"
              >
                {forgotLoading
                  ? t("A enviar...")
                  : t("Enviar link de recuperação")}
              </button>
            </form>

            <div className="mt-5 text-center">
              <button
                type="button"
                className="text-sm text-gray-600 hover:underline"
                onClick={closeForgotPasswordModal}
                disabled={forgotLoading}
              >
                {t("Voltar ao login")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}